import { useQuery } from '@tanstack/react-query'
import { RunDeliveryLink } from './RunDeliveryLink'
import { supabase } from '@/lib/supabase'
import { summariseRunFed, type LinkedDelivery } from '@/lib/runFed'
import { POLL } from '@/lib/pollTiers'

type Embed = LinkedDelivery | LinkedDelivery[] | null

/** UF-04: "Fed so far" on the run's Overview, above the tiles. Shows on an
 *  Active or Tipping-stopped run only; nothing when no delivery is linked.
 *  Pending shows nothing and a failed read says so (never a confident 0). */
export function RunFedLine({ runId, status }: { runId: string; status: string }) {
  const live = status === 'ACTIVE' || status === 'TIPPING_COMPLETE'
  const q = useQuery({
    queryKey: ['run-fed', runId],
    enabled: live,
    refetchInterval: POLL.standard,
    queryFn: async () => {
      const { data, error } = await supabase.from('run_deliveries')
        .select('delivery_id, deliveries(delivery_id, delivery_note_number, client_ref_no, bins_remaining)')
        .eq('run_id', runId)
        .range(0, 999)
      if (error) throw error
      const rows: LinkedDelivery[] = []
      for (const r of (data ?? []) as unknown as { deliveries: Embed }[]) {
        const d = Array.isArray(r.deliveries) ? r.deliveries[0] : r.deliveries
        if (d) rows.push(d)
      }
      return rows
    },
  })
  if (!live) return null
  if (q.isError) {
    return <div className="app-card p-3 text-caption text-fg-muted" data-testid="run-fed">Could not read the linked deliveries. Try again in a moment.</div>
  }
  if (!q.data) return null
  const fed = summariseRunFed(q.data)
  if (!fed) return null
  return (
    <div id="run-fed" className="app-card p-3 space-y-1" data-testid="run-fed">
      <p className="text-body font-semibold text-fg">
        Fed so far <span className="font-normal text-fg-muted">· {fed.tippedOut} of {fed.total} linked {fed.total === 1 ? 'delivery' : 'deliveries'} tipped out</span>
      </p>
      {fed.waiting.length > 0 && (
        <p className="text-caption text-fg-muted">
          Waiting to tip:{' '}
          {fed.waiting.map((w, i) => (
            <span key={w.delivery_id}>
              {i > 0 && ' · '}
              <RunDeliveryLink deliveryId={w.delivery_id}>DN {w.label}</RunDeliveryLink>
              {' '}· {w.bins} {w.bins === 1 ? 'bin' : 'bins'}
            </span>
          ))}
        </p>
      )}
    </div>
  )
}
